// ── participants-collection: useCollection server adapter for /participants ─

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  useCollection,
  type CollectionColumnDef,
  type CollectionFetcherInput,
  type CollectionFetcherResult,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";
import { destructiveButtonClass } from "../../components/ui-classes";
import type { CoachingParticipantDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import {
  isRemovedParticipant,
  orDash,
  PARTICIPANT_LIST_STATUSES,
  participantStatusLabel,
  participantStatusTone
} from "./participants-helpers";

export const PARTICIPANTS_PAGE_SIZE = 25;

export type ParticipantRow = CoachingParticipantDto & Record<string, unknown>;

export interface CohortFilterOption {
  id: string;
  label: string;
}

export interface ParticipantsCollectionActions {
  allowUpdate: boolean;
  onEdit: (row: CoachingParticipantDto) => void;
  onRemove: (row: CoachingParticipantDto) => void;
}

export function cohortOptionsFromRows(rows: CoachingParticipantDto[]): CohortFilterOption[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const id = (row.cohort_id ?? "").trim();
    if (!id) continue;
    if (!seen.has(id)) seen.set(id, (row.cohort_name ?? "").trim() || id);
  }
  return Array.from(seen, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
}

export function buildParticipantsFilterDefs(
  rows: CoachingParticipantDto[],
  cohortOptionsOverride?: CohortFilterOption[]
): CollectionFilterDef<ParticipantRow>[] {
  const cohortOptions = cohortOptionsOverride ?? cohortOptionsFromRows(rows);
  return [
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "active",
      options: PARTICIPANT_LIST_STATUSES.map((status) => ({
        value: status,
        label: participantStatusLabel(status)
      }))
    },
    {
      id: "cohort_id",
      label: "Cohort",
      type: "select",
      placeholder: "All cohorts",
      getValue: (row) => row.cohort_id ?? "",
      options: cohortOptions.map((option) => ({ value: option.id, label: option.label }))
    }
  ];
}

export function buildParticipantsCollectionColumns(
  actions: ParticipantsCollectionActions
): CollectionColumnDef<ParticipantRow>[] {
  const columns: CollectionColumnDef<ParticipantRow>[] = [
    {
      id: "name",
      header: "Participant",
      searchValue: (row) => `${row.name ?? ""} ${row.email ?? ""}`,
      // `priority` drives the responsive layout: lower-priority columns collapse behind
      // "Expand row details" on mobile. Sorting stays off: the server pages this list.
      priority: 1,
      cell: (row) => (
        <Link
          to={`/participants/${encodeURIComponent(row.id)}`}
          className="font-medium text-link no-underline hover:underline"
          data-exact-set-name="Open participant">
          {row.name || row.email || "Unnamed participant"}
        </Link>
      )
    },
    {
      id: "email",
      header: "Email",
      priority: 2,
      cell: (row) => orDash(row.email)
    },
    {
      id: "cohort_name",
      header: "Cohort",
      priority: 2,
      cell: (row) => orDash(row.cohort_name)
    },
    {
      id: "company_name",
      header: "Company",
      priority: 3,
      cell: (row) => orDash(row.company_name)
    },
    {
      id: "status",
      header: "Status",
      priority: 2,
      cell: (row) => (
        <Badge variant={participantStatusTone(row.status)}>{participantStatusLabel(row.status)}</Badge>
      )
    }
  ];

  if (actions.allowUpdate) {
    columns.push({
      id: "_actions",
      header: "",
      sortable: false,
      priority: 1,
      cell: (row) => isRemovedParticipant(row) ? null : (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onEdit(row)}
            aria-label={`Edit ${row.name || row.email || row.id}`}
            data-exact-set-name="Edit participant">
            Edit
          </Button>
          {row.cohort_id ? (
            <Button
              variant="danger"
              size="sm"
              className={destructiveButtonClass}
              onClick={() => actions.onRemove(row)}
              aria-label={`Remove ${row.name || row.email || row.id}`}
              data-exact-set-name="Remove participant">
              Remove
            </Button>
          ) : null}
        </div>
      )
    });
  }

  return columns;
}

export type ParticipantsCollectionFetcher = (
  input: CollectionFetcherInput
) => Promise<CollectionFetcherResult<ParticipantRow>>;

export function useParticipantsCollection(
  fetcher: ParticipantsCollectionFetcher,
  actions: ParticipantsCollectionActions,
  cohortOptions: CohortFilterOption[] = [],
  routePath?: string
): UseCollectionResult<ParticipantRow> {
  const filters = useMemo(() => buildParticipantsFilterDefs([], cohortOptions), [cohortOptions]);
  const columns = useMemo(() => buildParticipantsCollectionColumns(actions), [actions]);
  const initialState = useMemo(
    () => readDirectoryInitialState(routePath, PARTICIPANTS_PAGE_SIZE),
    [routePath]
  );

  return useCollection<ParticipantRow>({
    mode: "server",
    fetcher,
    columns,
    filters,
    pageSize: PARTICIPANTS_PAGE_SIZE,
    keepRowsWhileRefreshing: true,
    urlSync: "query",
    views: "coaching-participants",
    initialState,
    getRowKey: (row) => row.id
  });
}
